// src/scoring.ts
var DEFAULT_SCORING_SCHEME = {
  sightNose: { min: 1, max: 3 },
  taste: { min: 1, max: 3 },
  overall: { min: 1, max: 4 }
};
function maxTotal(scheme = DEFAULT_SCORING_SCHEME) {
  return scheme.sightNose.max + scheme.taste.max + scheme.overall.max;
}
function computeTotal(components) {
  const present = [components.sightNose, components.taste, components.overall].filter(
    (v) => typeof v === "number" && Number.isFinite(v)
  );
  if (present.length === 0) return null;
  return present.reduce((sum, v) => sum + v, 0);
}
function validateComponents(components, scheme = DEFAULT_SCORING_SCHEME) {
  const issues = [];
  const check = (field, range) => {
    const value = components[field];
    if (value == null) return;
    if (!Number.isFinite(value) || !Number.isInteger(value * 4)) {
      issues.push({ field, message: `${field} must use quarter-point increments` });
    } else if (value < range.min || value > range.max) {
      issues.push({
        field,
        message: `${field} must be between ${range.min} and ${range.max}`
      });
    }
  };
  check("sightNose", scheme.sightNose);
  check("taste", scheme.taste);
  check("overall", scheme.overall);
  return issues;
}
function assignRanksDescending(items, valueOf) {
  const withValue = items.map((item, index) => {
    const raw = valueOf(item);
    const value = typeof raw === "number" && Number.isFinite(raw) ? raw : null;
    return { item, value, index };
  });
  const scored = withValue.filter((e) => e.value !== null).sort((a, b) => b.value - a.value || a.index - b.index);
  const rankByIndex = /* @__PURE__ */ new Map();
  let currentRank = 0;
  let seen = 0;
  let prevValue = null;
  for (const entry of scored) {
    seen += 1;
    if (prevValue === null || entry.value < prevValue) {
      currentRank = seen;
    }
    rankByIndex.set(entry.index, currentRank);
    prevValue = entry.value;
  }
  return withValue.map(({ item, value, index }) => ({
    item,
    value,
    rank: value === null ? null : rankByIndex.get(index) ?? null
  }));
}
function aggregateEventScores(scores) {
  const totalsByWine = /* @__PURE__ */ new Map();
  for (const s of scores) {
    if (s.total == null) continue;
    const list = totalsByWine.get(s.wineId) ?? [];
    list.push(s.total);
    totalsByWine.set(s.wineId, list);
  }
  const wineIds = [...new Set(scores.map((s) => s.wineId))];
  const wineScores = wineIds.map((wineId) => {
    const totals = totalsByWine.get(wineId) ?? [];
    const groupScore = totals.reduce((a, b) => a + b, 0);
    return {
      wineId,
      groupScore,
      groupAverage: totals.length ? groupScore / totals.length : null,
      scoreCount: totals.length
    };
  });
  const ranked = assignRanksDescending(wineScores, (w) => w.scoreCount > 0 ? w.groupScore : null);
  const wines = ranked.map((r) => ({
    wineId: r.item.wineId,
    groupScore: r.item.groupScore,
    groupAverage: r.item.groupAverage,
    scoreCount: r.item.scoreCount,
    groupRank: r.rank
  }));
  const rowsByPerson = /* @__PURE__ */ new Map();
  for (const s of scores) {
    const list = rowsByPerson.get(s.personId) ?? [];
    list.push(s);
    rowsByPerson.set(s.personId, list);
  }
  const memberRanks = [];
  for (const [personId, rows] of rowsByPerson) {
    const rankedRows = assignRanksDescending(rows, (r) => r.total);
    for (const rr of rankedRows) {
      memberRanks.push({ personId, wineId: rr.item.wineId, rank: rr.rank });
    }
  }
  return { wines, memberRanks };
}

// src/roles.ts
var ROLES = [
  "site_admin",
  "tasting_admin",
  "records_admin",
  "records_contributor",
  "user"
];
var ROLE_LABELS = {
  site_admin: "Super Admin",
  tasting_admin: "Tasting Admin",
  records_admin: "Records Admin",
  records_contributor: "Records Contributor",
  user: "User"
};
var PERMISSIONS = {
  "users:manage": ["site_admin"],
  "roles:grant": ["site_admin"],
  "members:manage": ["site_admin"],
  "payments:manage": ["site_admin"],
  /** Compose and send club-wide messages (the broadcast composer). */
  "comms:send": ["site_admin"],
  /** Message the people attached to one tasting (from its own page). */
  "comms:event": ["site_admin", "tasting_admin"],
  "tasting:create": ["site_admin", "tasting_admin"],
  "tasting:run": ["site_admin", "tasting_admin"],
  "records:approve": ["site_admin", "records_admin"],
  "records:contribute": ["site_admin", "records_admin", "records_contributor"],
  "import:run": ["site_admin", "records_admin"],
  /**
   * The Setup lists: rename, merge and proofread producers, regions, vineyards
   * and varietals. Members may ADD to them from the RSVP form; only these roles
   * tidy them up.
   */
  "lookups:manage": ["site_admin", "tasting_admin", "records_admin"],
  /**
   * Contribute historical records (files, memories, partial data about past
   * tastings) into the review queue. Every signed-in person holds this — the
   * merge step, not the upload gate, protects the archive.
   */
  "history:contribute": ["site_admin", "tasting_admin", "records_admin", "records_contributor", "user"],
  "archive:view": ["site_admin", "tasting_admin", "records_admin", "records_contributor", "user"],
  "tasting:score": ["site_admin", "tasting_admin", "records_admin", "records_contributor", "user"]
};
function hasRole(userRoles, role) {
  return userRoles.includes(role);
}
function can(userRoles, permission) {
  const allowed = PERMISSIONS[permission];
  return userRoles.some((role) => allowed.includes(role));
}

// src/constants.ts
var MEMBER_TYPES = [
  "founding_member",
  "friends_of_the_gentlemen",
  "in_consideration",
  "in_memoriam",
  "member_at_large",
  "member_general",
  "past_member"
];
var MEMBER_TYPE_LABELS = {
  founding_member: "Founding Member",
  friends_of_the_gentlemen: "Friends of the Gentlemen",
  in_consideration: "In Consideration",
  in_memoriam: "In Memoriam",
  member_at_large: "Member at Large",
  member_general: "Member General",
  past_member: "Past Member"
};
var DEFAULT_INVITED_MEMBER_TYPES = [
  "member_general",
  "founding_member",
  "member_at_large"
];
var GUEST_MEMBER_TYPES = [
  "friends_of_the_gentlemen",
  "in_consideration"
];
var INACTIVE_MEMBER_TYPES = ["past_member", "in_memoriam"];
function isInactiveMemberType(type) {
  return !!type && INACTIVE_MEMBER_TYPES.includes(type);
}
function isGuestMemberType(type) {
  return !!type && GUEST_MEMBER_TYPES.includes(type);
}
function isInvitedMemberType(type, invited) {
  if (isInactiveMemberType(type)) return false;
  if (!type) return true;
  return invited.includes(type);
}
function memberTypeLabel(type) {
  if (!type) return null;
  return MEMBER_TYPE_LABELS[type] ?? type;
}
var ACCOUNT_STATUSES = ["invited", "active", "suspended"];
var DUES_STATUSES = ["pending", "paid", "waived", "cancelled"];
var PAYMENT_METHODS = ["cash", "check", "card", "paypal", "other"];
var EVENT_STATUSES = ["upcoming", "live", "completed", "archived"];
var EVENT_PHASES = [
  "setup",
  "scoring_open",
  "scoring_closed",
  "revealing",
  "final"
];
var DATE_PRECISIONS = ["day", "month", "year", "unknown"];
var RECORD_STATUSES = ["pending", "approved", "rejected"];
var MEDIA_TYPES = ["photo", "menu", "scorecard", "label"];
var IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif", "avif"];
var IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"];
var MAX_IMAGE_BYTES = 8 * 1024 * 1024;
var IMPORT_EXTENSIONS = ["csv", "xls", "xlsx"];
var IMPORT_MIME_TYPES = [
  "text/csv",
  "application/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/octet-stream"
  // some browsers send this for .xlsx/.csv
];
var MAX_IMPORT_BYTES = 25 * 1024 * 1024;
var IMPORT_MATCH_THRESHOLD = 0.35;
var HISTORY_CONTRIBUTION_STATUSES = [
  "draft",
  // being assembled by the contributor
  "submitted",
  // waiting for a Records Admin
  "needs_info",
  // sent back with a question; contributor may edit + resubmit
  "merged",
  // folded into the archive (event/wines/scores/media)
  "declined"
];
var CONTRIBUTION_FILE_KINDS = [
  "photo",
  "scorecard",
  "menu",
  "label",
  "spreadsheet",
  "document",
  "other"
];
var DOCUMENT_EXTENSIONS = ["pdf", "doc", "docx", "txt"];
var CONTRIBUTION_EXTENSIONS = [
  ...IMAGE_EXTENSIONS,
  ...IMPORT_EXTENSIONS,
  ...DOCUMENT_EXTENSIONS
];
var MAX_CONTRIBUTION_BYTES = MAX_IMPORT_BYTES;
var MAX_CONTRIBUTION_FILES = 50;
var IMPORT_BATCH_STATUSES = [
  "uploaded",
  // file archived, not yet processed
  "pending",
  "processing",
  "completed",
  "failed"
];
var STAGING_ROW_STATUSES = [
  "pending",
  "validated",
  "approved",
  "rejected",
  "promoted",
  "skipped"
];
var OTP_TTL_MINUTES = 15;
var INVITE_LINK_TTL_HOURS = 72;
var WINE_COLORS = [
  "red",
  "white",
  "rose",
  "sparkling",
  "orange",
  "dessert",
  "other"
];
var WINE_LETTERS = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T"
];
var API_PREFIX = "/api/v1";

// src/comms.ts
import { z } from "zod";
var MESSAGE_CATEGORIES = [
  "announcements",
  "eventReminders",
  "rsvpUpdates",
  "liveTasting",
  "dues"
];
var MESSAGE_CATEGORY_LABELS = {
  announcements: "Club announcements",
  eventReminders: "Event reminders",
  rsvpUpdates: "RSVP updates",
  liveTasting: "Live tasting alerts",
  dues: "Dues & billing"
};
var MESSAGE_KINDS = [
  "announcement",
  "event.new",
  "event.reminder",
  /** An ad-hoc leadership message about one tasting (final details, thank-you…). */
  "event.message",
  /** The tasting opened to more member types: `event.invite:{eventId}:{memberType}`. */
  "event.invite",
  /** Reminder-day nudge to registrants who haven't paid: `event.reminder.payment:{eventId}:{days}`. */
  "event.reminder.payment",
  /** Reminder-day nudge to registrants with no wine: `event.reminder.wine:{eventId}:{days}`. */
  "event.reminder.wine",
  /** An admin's dashboard nudge (see NUDGE_REASONS). */
  "event.nudge",
  "tasting.live",
  "tasting.recap",
  /** The raw results summary (summaries route). */
  "tasting.summary",
  /** The personalised wrap-up: `tasting.wrapup:{eventId}:{personId}`. */
  "tasting.wrapup",
  "rsvp.confirmed",
  "rsvp.waitlist",
  "rsvp.promoted",
  "rsvp.cancelled",
  "dues.charged",
  "dues.reminder",
  "dues.receipt"
];
var MESSAGE_ORIGINS = ["leadership", "system"];
var MESSAGE_STATUSES = [
  "draft",
  "scheduled",
  "queued",
  "sending",
  "sent",
  "failed",
  "cancelled"
];
var DELIVERY_CHANNELS = ["inbox", "push", "email"];
var SENDABLE_CHANNELS = ["push", "email"];
var DELIVERY_STATUSES = [
  "pending",
  "sending",
  "sent",
  // handed to the provider
  "delivered",
  // provider confirmed (Expo receipt / SendGrid delivered event)
  "bounced",
  // the mailbox rejected it (SendGrid bounce/dropped)
  "failed",
  "skipped"
];
var channelPreferenceSchema = z.object({
  push: z.boolean(),
  email: z.boolean()
});
var notificationPreferencesSchema = z.object({
  announcements: channelPreferenceSchema,
  eventReminders: channelPreferenceSchema,
  rsvpUpdates: channelPreferenceSchema,
  liveTasting: channelPreferenceSchema,
  dues: channelPreferenceSchema
});
var DEFAULT_NOTIFICATION_PREFERENCES = {
  announcements: { push: true, email: true },
  eventReminders: { push: true, email: true },
  rsvpUpdates: { push: true, email: true },
  liveTasting: { push: true, email: false },
  dues: { push: true, email: true }
};
function parseNotificationPreferences(raw) {
  const out = {
    announcements: { ...DEFAULT_NOTIFICATION_PREFERENCES.announcements },
    eventReminders: { ...DEFAULT_NOTIFICATION_PREFERENCES.eventReminders },
    rsvpUpdates: { ...DEFAULT_NOTIFICATION_PREFERENCES.rsvpUpdates },
    liveTasting: { ...DEFAULT_NOTIFICATION_PREFERENCES.liveTasting },
    dues: { ...DEFAULT_NOTIFICATION_PREFERENCES.dues }
  };
  if (!raw || typeof raw !== "object") return out;
  for (const key of MESSAGE_CATEGORIES) {
    const v = raw[key];
    if (typeof v === "boolean") {
      out[key].push = v;
    } else if (v && typeof v === "object") {
      const o = v;
      if (typeof o.push === "boolean") out[key].push = o.push;
      if (typeof o.email === "boolean") out[key].email = o.email;
    }
  }
  return out;
}
var audienceRuleSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("all_members") }),
  z.object({ type: z.literal("all_users") }),
  z.object({ type: z.literal("role"), roles: z.array(z.enum(ROLES)).min(1) }),
  z.object({ type: z.literal("member_type"), memberTypes: z.array(z.enum(MEMBER_TYPES)).min(1) }),
  z.object({
    type: z.literal("rsvp"),
    eventId: z.string().uuid(),
    statuses: z.array(z.enum(["registered", "waitlist", "cancelled"])).min(1)
  }),
  z.object({ type: z.literal("not_rsvpd"), eventId: z.string().uuid() }),
  /** Active members whose member type the tasting is offered to (events.invited_member_types). */
  z.object({ type: z.literal("invited"), eventId: z.string().uuid() }),
  /** Registered, payment outstanding (payment_status = 'unpaid'). */
  z.object({ type: z.literal("rsvp_unpaid"), eventId: z.string().uuid() }),
  /** Registered with no wine entered (no Cellar link and no free-text name). */
  z.object({ type: z.literal("rsvp_no_wine"), eventId: z.string().uuid() }),
  /** Registered with a wine but no label photo. */
  z.object({ type: z.literal("rsvp_no_label"), eventId: z.string().uuid() }),
  z.object({ type: z.literal("attended"), eventId: z.string().uuid() }),
  z.object({
    type: z.literal("attended_any"),
    sinceYear: z.number().int().min(1900).max(2100).optional(),
    minEvents: z.number().int().min(1).max(500).optional()
  }),
  z.object({
    type: z.literal("dues_outstanding"),
    periodYear: z.number().int().min(1900).max(2100).optional(),
    overdueOnly: z.boolean().optional()
  }),
  z.object({ type: z.literal("people"), personIds: z.array(z.string().uuid()).min(1).max(2e3) })
]);
var audienceSpecSchema = z.object({
  /** OR-ed together (UNION). */
  include: z.array(audienceRuleSchema).min(1).max(20),
  /** Subtracted from the result (EXCEPT). */
  exclude: z.array(audienceRuleSchema).max(20).optional()
});
var AUDIENCE_RULE_LABELS = {
  all_members: "All members",
  all_users: "Everyone with an account",
  role: "By role",
  member_type: "By member type",
  rsvp: "RSVP status for a tasting",
  not_rsvpd: "Hasn't RSVP'd to a tasting",
  invited: "Invited to a tasting",
  rsvp_unpaid: "Registered but hasn't paid",
  rsvp_no_wine: "Registered but hasn't entered a wine",
  rsvp_no_label: "Registered without a label photo",
  attended: "Attended a tasting",
  attended_any: "Regulars",
  dues_outstanding: "Dues outstanding",
  people: "Hand-picked members"
};
var ALL_MEMBERS = { include: [{ type: "all_members" }] };
var justPeople = (...personIds) => ({
  include: [{ type: "people", personIds }]
});
var composeMessageSchema = z.object({
  /** Template the composer started from — recorded for the history view. */
  templateId: z.string().uuid().optional(),
  title: z.string().trim().min(1).max(140),
  body: z.string().trim().min(1).max(5e3),
  /** Optional rich HTML fragment for the email channel. */
  bodyHtml: z.string().max(2e4).optional(),
  /** In-app deep link, e.g. `/tastings/:id`. Relative paths only. */
  link: z.string().max(500).regex(/^\/[^\s]*$/, "Must be a relative path starting with /").optional(),
  category: z.enum(MESSAGE_CATEGORIES).default("announcements"),
  channels: z.array(z.enum(SENDABLE_CHANNELS)).max(2).default(["push", "email"]),
  audience: audienceSpecSchema,
  /** ISO instant. Omit to send now. */
  scheduledFor: z.string().datetime().optional(),
  /**
   * Ties the message to a tasting. Event merge fields (`{{eventTitle}}` …) are
   * resolved from it server-side, and it shows up on the tasting's comms tab.
   */
  eventId: z.string().uuid().optional()
});
var audiencePreviewSchema = z.object({ audience: audienceSpecSchema });
var MERGE_FIELDS = [
  { key: "firstName", label: "First name", scope: "person", sample: "James" },
  { key: "lastName", label: "Last name", scope: "person", sample: "Whitmore" },
  { key: "fullName", label: "Full name", scope: "person", sample: "James Whitmore" },
  { key: "clubName", label: "Club name", scope: "club", sample: "Gentlemen's Wine Club" },
  { key: "appLink", label: "Link to the app", scope: "club", sample: "https://club.example.org" },
  { key: "eventTitle", label: "Tasting title", scope: "event", sample: "Right Bank Bordeaux" },
  { key: "eventDate", label: "Tasting date", scope: "event", sample: "Thursday, November 12, 2026" },
  { key: "eventTheme", label: "Theme", scope: "event", sample: "Right Bank Bordeaux" },
  { key: "eventVenue", label: "Venue", scope: "event", sample: "L'Escargot" },
  { key: "eventLocation", label: "Region / location", scope: "event", sample: "Carmel" },
  { key: "eventDress", label: "Dress code", scope: "event", sample: "Jacket and tie" },
  { key: "eventPrice", label: "Price", scope: "event", sample: "$85" },
  { key: "eventCapacity", label: "Capacity", scope: "event", sample: "16" },
  { key: "eventLink", label: "Link to the tasting", scope: "event", sample: "https://club.example.org/tastings/\u2026" }
];
var MERGE_TOKEN = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;
function applyMergeFields(text, vars, opts = {}) {
  return text.replace(MERGE_TOKEN, (whole, key) => {
    const v = vars[key];
    if (v != null && v !== "") return v;
    return opts.strip ? "" : whole;
  });
}
function mergeFieldsUsed(text) {
  const out = /* @__PURE__ */ new Set();
  for (const m of text.matchAll(MERGE_TOKEN)) out.add(m[1]);
  return [...out];
}
var SAMPLE_MERGE_VARS = Object.fromEntries(
  MERGE_FIELDS.map((f) => [f.key, f.sample])
);
var TEMPLATE_SCOPES = ["general", "event"];
var EVENT_AUDIENCE_PRESETS = [
  "registered",
  "waitlist",
  "registered_and_waitlist",
  "not_rsvpd",
  "attended",
  "all_members",
  "invited",
  "unpaid",
  "no_wine"
];
var EVENT_AUDIENCE_LABELS = {
  registered: "Registered (holding a seat)",
  waitlist: "On the waitlist",
  registered_and_waitlist: "Registered + waitlist",
  not_rsvpd: "Hasn't RSVP'd yet",
  attended: "Attended (marked present)",
  all_members: "All members",
  invited: "Everyone invited to this tasting",
  unpaid: "Registered, hasn't paid",
  no_wine: "Registered, hasn't entered a wine"
};
function eventAudienceSpec(preset, eventId) {
  switch (preset) {
    case "registered":
      return { include: [{ type: "rsvp", eventId, statuses: ["registered"] }] };
    case "waitlist":
      return { include: [{ type: "rsvp", eventId, statuses: ["waitlist"] }] };
    case "registered_and_waitlist":
      return { include: [{ type: "rsvp", eventId, statuses: ["registered", "waitlist"] }] };
    case "not_rsvpd":
      return { include: [{ type: "not_rsvpd", eventId }] };
    case "attended":
      return { include: [{ type: "attended", eventId }] };
    case "all_members":
      return ALL_MEMBERS;
    case "invited":
      return { include: [{ type: "invited", eventId }] };
    case "unpaid":
      return { include: [{ type: "rsvp_unpaid", eventId }] };
    case "no_wine":
      return { include: [{ type: "rsvp_no_wine", eventId }] };
  }
}
var eventInviteSchema = z.object({
  memberTypes: z.array(z.enum(MEMBER_TYPES)).min(1).max(MEMBER_TYPES.length),
  announce: z.boolean().default(true)
});
var templateInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).optional(),
  scope: z.enum(TEMPLATE_SCOPES).default("general"),
  category: z.enum(MESSAGE_CATEGORIES).default("announcements"),
  subject: z.string().trim().min(1).max(140),
  body: z.string().trim().min(1).max(5e3),
  channels: z.array(z.enum(SENDABLE_CHANNELS)).max(2).default(["push", "email"]),
  /** Default audience for event templates; ignored for general ones. */
  eventAudience: z.enum(EVENT_AUDIENCE_PRESETS).optional()
});
var updateTemplateSchema = templateInputSchema.partial();
var DEFAULT_TEMPLATES = [
  {
    slug: "event-announcement",
    name: "New tasting announced",
    description: "Sent automatically to the invited member types when a tasting is created (or from the tasting page).",
    scope: "event",
    category: "announcements",
    subject: "Save the date \u2014 {{eventTitle}}, {{eventDate}}",
    body: "Gentlemen,\n\nOur next tasting is on the calendar: {{eventTitle}}, {{eventDate}}{{eventVenue}}.\n\nSeats are limited and go to the first to reply, so please RSVP in the app as soon as you can.\n\n{{eventLink}}",
    channels: ["push", "email"],
    eventAudience: "invited"
  },
  {
    slug: "event-reminder-rsvp",
    name: "RSVP reminder",
    description: "The automatic nudge to members who have not yet replied, sent on the reminder days set on each tasting.",
    scope: "event",
    category: "eventReminders",
    subject: "Reminder: RSVP for {{eventTitle}} ({{eventDate}})",
    body: "Dear {{firstName}},\n\nWe haven't yet heard whether you'll join us for {{eventTitle}} on {{eventDate}}. A quick reply in the app holds your seat \u2014 or lets us offer it to someone on the waitlist.\n\n{{eventLink}}",
    channels: ["push", "email"],
    eventAudience: "not_rsvpd"
  },
  {
    slug: "event-reminder-payment",
    name: "Payment reminder",
    description: 'Sent on the reminder days to registered members who have not paid, and by the "Not paid" nudge on the tasting dashboard.',
    scope: "event",
    category: "eventReminders",
    subject: "Your seat at {{eventTitle}} is waiting on payment",
    body: "Dear {{firstName}},\n\nThank you for registering for {{eventTitle}} on {{eventDate}}. Your seat is confirmed with your payment ({{eventPrice}}), which you can make in the app in a minute.\n\n{{eventLink}}",
    channels: ["push", "email"],
    eventAudience: "unpaid"
  },
  {
    slug: "event-reminder-wine",
    name: "Wine details reminder",
    description: 'Sent on the reminder days to registered members who have not entered their wine, and by the "No wine" / "No label photo" nudges.',
    scope: "event",
    category: "eventReminders",
    subject: "What are you bringing to {{eventTitle}}?",
    body: "Dear {{firstName}},\n\nWe're looking forward to seeing you at {{eventTitle}} on {{eventDate}}. Please add the wine you're bringing in the app \u2014 vintage, producer, region \u2014 and a photo of the label, so it's ready for the reveal.\n\n{{eventLink}}",
    channels: ["push", "email"],
    eventAudience: "no_wine"
  },
  {
    slug: "event-wrapup",
    name: "Tasting wrap-up",
    description: "The subject and opening lines of the post-tasting wrap-up; the results table and each member's own scores are added automatically.",
    scope: "event",
    category: "announcements",
    subject: "{{eventTitle}} \u2014 the results",
    body: "Gentlemen,\n\nThank you for a splendid evening. The results are below.",
    channels: ["email"],
    eventAudience: "attended"
  },
  {
    slug: "event-final-details",
    name: "Final details for attendees",
    description: "Time, venue, dress and what to bring. Send to registered members a day or two before.",
    scope: "event",
    category: "eventReminders",
    subject: "Final details \u2014 {{eventTitle}} on {{eventDate}}",
    body: "Dear {{firstName}},\n\nWe look forward to seeing you at {{eventTitle}} on {{eventDate}}.\n\nVenue: {{eventVenue}}\nDress: {{eventDress}}\n\nPlease arrive a few minutes early so we can pour promptly. If your plans have changed, do let us know in the app so your seat can go to the waitlist.\n\n{{eventLink}}",
    channels: ["push", "email"],
    eventAudience: "registered"
  },
  {
    slug: "event-waitlist-update",
    name: "Waitlist update",
    description: "Keep the waitlist informed when seats are tight or likely to open.",
    scope: "event",
    category: "rsvpUpdates",
    subject: "Waitlist update \u2014 {{eventTitle}}",
    body: "Dear {{firstName}},\n\nYou're on the waitlist for {{eventTitle}} on {{eventDate}}. We'll confirm your seat automatically the moment one opens, and you'll hear from us straight away.\n\n{{eventLink}}",
    channels: ["push", "email"],
    eventAudience: "waitlist"
  },
  {
    slug: "event-thank-you",
    name: "Thank you for attending",
    description: "The morning-after note to everyone who came.",
    scope: "event",
    category: "announcements",
    subject: "Thank you \u2014 {{eventTitle}}",
    body: "Dear {{firstName}},\n\nThank you for joining us for {{eventTitle}}. The full results and every wine poured are now in the archive.\n\n{{eventLink}}",
    channels: ["push", "email"],
    eventAudience: "attended"
  },
  {
    slug: "event-change",
    name: "Change of plans",
    description: "A date, venue or other change to a tasting people have already RSVP'd to.",
    scope: "event",
    category: "eventReminders",
    subject: "Change of plans \u2014 {{eventTitle}}",
    body: "Dear {{firstName}},\n\nA quick note about {{eventTitle}}: [describe the change]. The tasting is now on {{eventDate}} at {{eventVenue}}.\n\nYour RSVP still stands. If the new arrangements no longer suit you, please update it in the app.\n\n{{eventLink}}",
    channels: ["push", "email"],
    eventAudience: "registered_and_waitlist"
  },
  {
    slug: "general-announcement",
    name: "Club announcement",
    description: "A blank slate for news from the committee.",
    scope: "general",
    category: "announcements",
    subject: "A note from the {{clubName}}",
    body: "Gentlemen,\n\n[Your announcement here.]\n\n{{appLink}}",
    channels: ["push", "email"]
  },
  {
    slug: "welcome-new-member",
    name: "Welcome to the club",
    description: "For a newly admitted member, after their invitation has been accepted.",
    scope: "general",
    category: "announcements",
    subject: "Welcome to the {{clubName}}, {{firstName}}",
    body: "Dear {{firstName}},\n\nOn behalf of the membership, welcome to the {{clubName}}. We've tasted together since 1984 and we're glad to have you at the table.\n\nThe app is where tastings are announced and RSVPs taken, where you'll score on the night, and where forty years of results live.\n\n{{appLink}}",
    channels: ["email"]
  },
  {
    slug: "dues-renewal",
    name: "Annual dues renewal",
    description: 'The yearly renewal letter. Pair it with the "Dues outstanding" audience.',
    scope: "general",
    category: "dues",
    subject: "Your {{clubName}} membership renewal",
    body: "Dear {{firstName}},\n\nIt's that time of year: annual dues for the coming season are now due. You can see the amount and settle it in the app.\n\nThank you for another year of good company and better wine.\n\n{{appLink}}",
    channels: ["push", "email"]
  },
  {
    slug: "newsletter",
    name: "Newsletter",
    description: "The occasional round-up: recent results, what is coming, notes from the cellar.",
    scope: "general",
    category: "announcements",
    subject: "{{clubName}} \u2014 the latest",
    body: "Gentlemen,\n\nRecently poured\n[Highlights from the last tasting.]\n\nComing up\n[What is on the calendar.]\n\nFrom the cellar\n[A note or two.]\n\n{{appLink}}",
    channels: ["email"]
  }
];
var eventCommsSendSchema = z.object({
  templateId: z.string().uuid().optional(),
  title: z.string().trim().min(1).max(140),
  body: z.string().trim().min(1).max(5e3),
  audience: z.enum(EVENT_AUDIENCE_PRESETS),
  category: z.enum(MESSAGE_CATEGORIES).default("eventReminders"),
  channels: z.array(z.enum(SENDABLE_CHANNELS)).max(2).default(["push", "email"]),
  scheduledFor: z.string().datetime().optional(),
  /** Send only to the caller, for checking the copy. */
  test: z.boolean().default(false)
});

// src/schemas.ts
import { z as z5 } from "zod";

// src/safeUrl.ts
import { z as z2 } from "zod";
var safeHttpUrlSchema = z2.string().trim().max(2e3).refine(
  (v) => {
    if (/^\/api\/v1\/media\/media\/[a-zA-Z0-9_./-]+$/.test(v) && !v.includes("..")) return true;
    try {
      const u = new URL(v);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  },
  { message: "URL must use http or https" }
);
var optionalSafeHttpUrlSchema = z2.preprocess(
  (v) => v === "" || v == null ? void 0 : v,
  safeHttpUrlSchema.optional()
);
var nullableSafeHttpUrlSchema = z2.preprocess(
  (v) => v === "" ? null : v,
  safeHttpUrlSchema.nullish()
);

// src/wineEntry.ts
import { z as z3 } from "zod";
var WINE_WORLDS = ["old", "new"];
var EVENT_WORLD_SCOPES = ["old", "new", "both"];
var WINE_WORLD_LABELS = {
  old: "Old World",
  new: "New World"
};
var EVENT_WORLD_SCOPE_LABELS = {
  old: "Old World only",
  new: "New World only",
  both: "Old and New World (member chooses)"
};
var WINE_FIELD_LABELS = {
  old: {
    vintage: "Vintage",
    producer: "Producer / Domaine",
    region: "Region / Commune",
    vineyard: "Vineyard (if named)",
    varietal: null,
    cuvee: "Cuv\xE9e (optional)"
  },
  new: {
    vintage: "Vintage",
    producer: "Producer / Winery",
    region: "Region / AVA",
    vineyard: "Vineyard (if named)",
    varietal: "Varietal",
    cuvee: "Wine name (optional)"
  }
};
var showsVarietal = (world) => world !== "old";
function effectiveWineWorld(scope, picked) {
  if (scope === "old" || scope === "new") return scope;
  return picked ?? null;
}
var lookupName = z3.string().trim().min(1).max(200);
var optionalLookupName = z3.string().trim().max(200).optional();
var lookupId = z3.string().uuid().optional();
var structuredWineInputSchema = z3.object({
  world: z3.enum(WINE_WORLDS).optional(),
  vintage: z3.number().int().min(1800).max(2100).nullable(),
  producerId: lookupId,
  producer: lookupName,
  regionId: lookupId,
  region: lookupName,
  vineyardId: lookupId,
  vineyard: optionalLookupName,
  /** New World only; dropped by the server for an Old World wine. */
  varietalId: lookupId,
  varietal: optionalLookupName,
  /** A named cuvée ("Insignia", "Reserve"). Optional; see `structuredWineName`. */
  cuvee: z3.string().trim().max(200).optional(),
  color: z3.enum(WINE_COLORS)
});
var clean = (s) => (s ?? "").trim().replace(/\s+/g, " ");
function structuredWineName(parts) {
  const cuvee = clean(parts.cuvee);
  if (cuvee) return cuvee;
  return composedWineName(parts);
}
function composedWineName(parts) {
  const region = clean(parts.region);
  const vineyard = clean(parts.vineyard);
  const varietal = parts.world === "old" ? "" : clean(parts.varietal);
  const pieces = parts.world === "old" ? [region, vineyard] : [vineyard, varietal];
  const name = pieces.filter(Boolean).join(" ");
  return name || region || varietal || "Wine";
}
function cuveeFromCatalogName(catalogName, parts) {
  const name = clean(catalogName);
  if (!name || name === composedWineName(parts)) return "";
  return name;
}
function structuredWineDisplay(parts) {
  const vintage = parts.vintage == null ? "NV" : String(parts.vintage);
  return [vintage, clean(parts.producer), structuredWineName(parts)].filter(Boolean).join(" ");
}
var FOLD_PAIRS = [
  ["\xE0\xE1\xE2\xE3\xE4\xE5\u0101", "a"],
  ["\xE7\u0107\u010D", "c"],
  ["\xE8\xE9\xEA\xEB\u0113\u011B", "e"],
  ["\xEC\xED\xEE\xEF\u012B", "i"],
  ["\xF1\u0144", "n"],
  ["\xF2\xF3\xF4\xF5\xF6\xF8\u014D", "o"],
  ["\xF9\xFA\xFB\xFC\u016B", "u"],
  ["\xFD\xFF", "y"],
  ["\u017E", "z"],
  ["\u0161", "s"],
  ["\u0153", "o"],
  ["\xE6", "a"],
  ["\u2019\u2018`\xB4", "'"]
];
var LOOKUP_FOLD_FROM = FOLD_PAIRS.map(([from]) => from).join("");
var LOOKUP_FOLD_TO = FOLD_PAIRS.map(([from, to]) => to.repeat(from.length)).join("");
function foldLookupName(name) {
  let out = "";
  for (const ch of name.trim().toLowerCase().replace(/\s+/g, " ")) {
    const i = LOOKUP_FOLD_FROM.indexOf(ch);
    out += i >= 0 ? LOOKUP_FOLD_TO[i] : ch;
  }
  return out;
}

// src/rsvp.ts
import { z as z4 } from "zod";
var REGISTRATION_STATUSES = ["registered", "waitlist", "cancelled"];
var REGISTRATION_PAYMENT_STATUSES = [
  "not_required",
  "unpaid",
  "paid",
  "waived",
  "refund_due",
  "refunded"
];
var REGISTRATION_PAYMENT_LABELS = {
  not_required: "No payment needed",
  unpaid: "Unpaid",
  paid: "Paid",
  waived: "Waived",
  refund_due: "Refund due",
  refunded: "Refunded"
};
var SETTLED_PAYMENT_STATUSES = ["not_required", "paid", "waived"];
var isPaymentSettled = (s) => !!s && SETTLED_PAYMENT_STATUSES.includes(s);
var RSVP_BLOCKED_REASONS = [
  "closed",
  // the tasting is not upcoming/live
  "not_invited",
  // member type isn't in `invitedMemberTypes`
  "dues_outstanding"
  // `requiresDuesPaid` and an annual-dues charge is pending
];
var RSVP_ERROR_CODES = [
  ...RSVP_BLOCKED_REASONS,
  "category_required",
  // the tasting has categories and none was picked
  "category_full",
  // 409: that category is at its limit
  "world_required",
  // the tasting takes both sides and the wine didn't say which
  "payment_not_required"
  // checkout requested for a free seat
];
var RSVP_BLOCKED_MESSAGES = {
  closed: "This tasting is no longer taking RSVPs.",
  not_invited: "This tasting has not been opened to your membership type yet. Please contact an admin.",
  dues_outstanding: "Please settle your annual dues before you RSVP."
};
var wineCategoryInputSchema = z4.object({
  /** Present to keep (and rename) an existing category; absent to create one. */
  id: z4.string().uuid().optional(),
  name: z4.string().trim().min(1).max(80),
  /** Null = no limit. */
  maxWines: z4.number().int().min(1).max(20).nullable(),
  color: z4.enum(WINE_COLORS).nullish(),
  position: z4.number().int().min(0).max(100).optional()
});
var uniqueCategoryNames = (cats) => new Set(cats.map((c) => c.name.trim().toLowerCase())).size === cats.length;
var wineCategoriesSchema = z4.array(wineCategoryInputSchema).max(12).refine(uniqueCategoryNames, { message: "Category names must be different" });
var putWineCategoriesSchema = z4.object({ categories: wineCategoriesSchema });
var adminRegisterSchema = z4.object({
  personId: z4.string().uuid(),
  wine: structuredWineInputSchema.optional(),
  categoryId: z4.string().uuid().nullish(),
  labelImageUrl: optionalSafeHttpUrlSchema,
  notes: z4.string().max(500).optional(),
  /** Default: `unpaid` when the event requires payment, else `not_required`. */
  paymentStatus: z4.enum(["not_required", "unpaid", "paid", "waived"]).optional(),
  /** Seat them even when the tasting is full (otherwise they're waitlisted). */
  overrideCapacity: z4.boolean().default(false),
  /** Allow a full wine category. */
  overrideQuota: z4.boolean().default(false),
  /** Send the usual RSVP confirmation to the person. */
  notify: z4.boolean().default(true)
});
var adminUpdateRegistrationSchema = z4.object({
  /** Moving to `cancelled` runs the same cancel path as the member's own (promotion included). */
  status: z4.enum(REGISTRATION_STATUSES).optional(),
  /** null clears the structured wine (and the derived wineName). */
  wine: structuredWineInputSchema.nullable().optional(),
  categoryId: z4.string().uuid().nullable().optional(),
  labelImageUrl: nullableSafeHttpUrlSchema,
  notes: z4.string().max(500).nullish(),
  paymentStatus: z4.enum(REGISTRATION_PAYMENT_STATUSES).optional(),
  overrideCapacity: z4.boolean().default(false),
  overrideQuota: z4.boolean().default(false)
});

// src/schemas.ts
var emailSchema = z5.string().trim().toLowerCase().email().max(254);
var passwordSchema = z5.string().min(10, "Use at least 10 characters").max(200);
var uuidSchema = z5.string().uuid();
var loginSchema = z5.object({
  email: emailSchema,
  password: z5.string().min(1),
  /** Stable per-browser or per-installation device id for OTP + consent. */
  deviceId: z5.string().trim().min(8).max(120).optional(),
  client: z5.enum(["web", "mobile"]).default("web")
});
var verifyOtpSchema = z5.object({
  challengeId: uuidSchema,
  code: z5.string().trim().length(6).regex(/^\d{6}$/, "Enter the 6-digit code"),
  deviceId: z5.string().trim().min(8).max(120),
  client: z5.enum(["web", "mobile"]).default("web")
});
var COOKIE_CONSENT_CHOICES = ["accepted", "essential"];
var deviceConsentSchema = z5.object({
  deviceId: z5.string().trim().min(8).max(120),
  choice: z5.enum(COOKIE_CONSENT_CHOICES)
});
var setupSchema = z5.object({
  email: emailSchema,
  password: passwordSchema,
  firstName: z5.string().trim().min(1).max(80),
  lastName: z5.string().trim().min(1).max(80)
});
var inviteMemberSchema = z5.object({
  email: emailSchema,
  firstName: z5.string().trim().min(1).max(80),
  lastName: z5.string().trim().min(1).max(80),
  memberType: z5.enum(MEMBER_TYPES).default("member_general"),
  roles: z5.array(z5.enum(ROLES)).min(1).default(["user"])
});
var inviteExistingPersonSchema = z5.object({
  email: emailSchema.optional(),
  memberType: z5.enum(MEMBER_TYPES).optional(),
  roles: z5.array(z5.enum(ROLES)).min(1).optional()
});
var acceptInviteSchema = z5.object({
  token: z5.string().min(10),
  password: passwordSchema,
  phone: z5.string().trim().max(40).optional(),
  deviceId: z5.string().trim().min(8).max(120).optional()
});
var updateProfileSchema = z5.object({
  firstName: z5.string().trim().min(1).max(80).optional(),
  lastName: z5.string().trim().min(1).max(80).optional(),
  phone: z5.string().trim().max(40).nullish()
});
var changePasswordSchema = z5.object({
  currentPassword: z5.string().min(1),
  newPassword: passwordSchema
});
var forgotPasswordSchema = z5.object({
  email: emailSchema
});
var resetPasswordSchema = z5.object({
  token: z5.string().min(10),
  password: passwordSchema
});
var resetPasswordOtpSchema = z5.object({
  challengeId: uuidSchema,
  code: z5.string().trim().length(6).regex(/^\d{6}$/, "Enter the 6-digit code"),
  password: passwordSchema
});
var adminUpdateMemberSchema = z5.object({
  firstName: z5.string().trim().min(1).max(80).optional(),
  lastName: z5.string().trim().min(1).max(80).optional(),
  email: emailSchema.nullish(),
  phone: z5.string().trim().max(40).nullish(),
  memberType: z5.enum(MEMBER_TYPES).nullish(),
  memberSince: z5.string().date().nullish(),
  accountStatus: z5.enum(ACCOUNT_STATUSES).nullish(),
  roles: z5.array(z5.enum(ROLES)).min(1).optional(),
  notes: z5.string().max(5e3).nullish()
});
var chargeDuesSchema = z5.object({
  label: z5.string().trim().min(1).max(160),
  amount: z5.number().nonnegative().max(1e6),
  periodYear: z5.number().int().min(1980).max(2100).nullish(),
  dueDate: z5.string().date().nullish(),
  status: z5.enum(DUES_STATUSES).default("pending"),
  notes: z5.string().max(2e3).nullish()
});
var updateDuesSchema = z5.object({
  label: z5.string().trim().min(1).max(160).optional(),
  amount: z5.number().nonnegative().max(1e6).optional(),
  status: z5.enum(DUES_STATUSES).optional(),
  dueDate: z5.string().date().nullish(),
  paidAt: z5.string().date().nullish(),
  paymentMethod: z5.enum(PAYMENT_METHODS).nullish(),
  notes: z5.string().max(2e3).nullish()
});
function dimension(range) {
  return z5.number().multipleOf(0.25, "Use quarter-point increments").min(range.min).max(range.max).nullable();
}
function makeScoreInputSchema(scheme = DEFAULT_SCORING_SCHEME) {
  return z5.object({
    wineId: uuidSchema,
    clientId: uuidSchema,
    /**
     * Device clock at the moment of the tap. The server keeps the newest write
     * per (wine, person), so a queued offline replay can never overwrite a
     * correction the member made later. Optional for older clients; when absent
     * the server falls back to server-time (last-write-wins, as before).
     */
    clientUpdatedAt: z5.string().datetime().optional(),
    sightNose: dimension(scheme.sightNose),
    taste: dimension(scheme.taste),
    overall: dimension(scheme.overall),
    notes: z5.string().max(2e3).optional(),
    shareNotes: z5.boolean().optional()
  });
}
var scoreInputSchema = makeScoreInputSchema();
var DEFAULT_REMINDER_DAYS = [14, 3];
var reminderDaysSchema = z5.array(z5.number().int().min(0).max(90)).max(6).transform((days) => [...new Set(days)].sort((a, b) => b - a));
var invitedMemberTypesSchema = z5.array(z5.enum(MEMBER_TYPES)).min(1).max(MEMBER_TYPES.length).transform((types) => [...new Set(types)]).refine(
  (types) => !types.some((t) => INACTIVE_MEMBER_TYPES.includes(t)),
  { message: "Past members and those in memoriam cannot be invited" }
);
var cancellationCutoffDaysSchema = z5.number().int().min(0).max(60);
var eventInputSchema = z5.object({
  title: z5.string().trim().min(1).max(200),
  theme: z5.string().trim().max(200).optional(),
  regionLocation: z5.string().trim().max(200).optional(),
  venue: z5.string().trim().max(200).optional(),
  eventYear: z5.number().int().min(1900).max(2100),
  eventMonth: z5.number().int().min(1).max(12).nullish(),
  eventDate: z5.string().date().nullish(),
  datePrecision: z5.enum(DATE_PRECISIONS).default("day"),
  isScored: z5.boolean().default(true),
  dressCode: z5.string().trim().max(120).optional(),
  price: z5.number().nonnegative().max(1e5).nullish(),
  capacity: z5.number().int().positive().max(1e3).nullish(),
  notes: z5.string().max(5e3).optional(),
  /** Broadcast the "new tasting" announcement to all members on create. */
  announce: z5.boolean().default(true),
  /** Days before the tasting to nudge members who haven't RSVP'd. */
  reminderDays: reminderDaysSchema.optional(),
  /**
   * A rehearsal. Everything works, nothing counts: excluded from the archive,
   * stats, exports and reports, and its messages reach only the people added
   * to the test roster. Flip it later with PUT /events/:id/test-mode.
   */
  isTest: z5.boolean().default(false),
  // ---- Tier 2 setup ----
  /** Which wines it takes: Old World, New World, or both (member picks per wine). */
  worldScope: z5.enum(EVENT_WORLD_SCOPES).default("both"),
  /** The theme's rules, shown on the RSVP. */
  rules: z5.string().trim().max(5e3).optional(),
  invitedMemberTypes: invitedMemberTypesSchema.default([...DEFAULT_INVITED_MEMBER_TYPES]),
  /** Pay-to-confirm via card; needs a price. */
  requiresPayment: z5.boolean().default(false),
  /** Block self-service RSVPs from members with pending annual dues. */
  requiresDuesPaid: z5.boolean().default(false),
  cancellationCutoffDays: cancellationCutoffDaysSchema.nullish(),
  /** Wine quotas, created with the tasting (edit later via PUT /events/:id/wine-categories). */
  wineCategories: wineCategoriesSchema.optional()
}).refine((v) => v.datePrecision !== "day" || !!v.eventDate, {
  message: 'A full date is required when precision is "day"',
  path: ["eventDate"]
}).refine((v) => !v.isScored || v.capacity == null || v.capacity <= 16, {
  message: "Scored tastings are limited to 16 attendees",
  path: ["capacity"]
}).refine((v) => !v.requiresPayment || v.price != null && v.price > 0, {
  message: "Set a price to take payment on RSVP",
  path: ["price"]
});
var updateEventSchema = z5.object({
  title: z5.string().trim().min(1).max(200).optional(),
  theme: z5.string().trim().max(200).nullish(),
  regionLocation: z5.string().trim().max(200).nullish(),
  venue: z5.string().trim().max(200).nullish(),
  eventYear: z5.number().int().min(1900).max(2100).optional(),
  eventMonth: z5.number().int().min(1).max(12).nullish(),
  eventDate: z5.string().date().nullish(),
  datePrecision: z5.enum(DATE_PRECISIONS).optional(),
  isScored: z5.boolean().optional(),
  dressCode: z5.string().trim().max(120).nullish(),
  price: z5.number().nonnegative().max(1e5).nullish(),
  capacity: z5.number().int().positive().max(1e3).nullish(),
  notes: z5.string().max(5e3).nullish(),
  reminderDays: reminderDaysSchema.optional(),
  // ---- Tier 2 setup (isTest is NOT here: see setTestModeSchema) ----
  worldScope: z5.enum(EVENT_WORLD_SCOPES).optional(),
  rules: z5.string().trim().max(5e3).nullish(),
  /** Widening this (e.g. adding in_consideration) does not announce by itself — see eventInviteSchema. */
  invitedMemberTypes: invitedMemberTypesSchema.optional(),
  /** Turning this on requires a price (on the event or in this patch); checked server-side. */
  requiresPayment: z5.boolean().optional(),
  requiresDuesPaid: z5.boolean().optional(),
  cancellationCutoffDays: cancellationCutoffDaysSchema.nullish()
}).refine((v) => v.datePrecision !== "day" || !("eventDate" in v) || !!v.eventDate, {
  message: 'A full date is required when precision is "day"',
  path: ["eventDate"]
});
var setTestModeSchema = z5.object({ isTest: z5.boolean() });
var testParticipantInputSchema = z5.object({
  personId: z5.string().uuid()
});
var rsvpInputSchema = z5.object({
  wineName: z5.string().trim().max(300).optional(),
  // Omitted = unchanged; '' or null = remove the photo (what app v1.0.8 sends
  // after "Remove image", and what it meant before RSVPs became upserts).
  labelImageUrl: nullableSafeHttpUrlSchema,
  guestsCount: z5.number().int().min(0).max(10).default(0),
  notes: z5.string().max(500).optional(),
  wine: structuredWineInputSchema.optional(),
  categoryId: uuidSchema.nullish()
});
var wineInputSchema = z5.object({
  letter: z5.enum(WINE_LETTERS),
  vintage: z5.number().int().min(1800).max(2100).nullish(),
  producer: z5.string().trim().max(200).optional(),
  varietal: z5.string().trim().max(200).optional(),
  regionAva: z5.string().trim().max(200).optional(),
  vineyard: z5.string().trim().max(200).optional(),
  wineName: z5.string().trim().max(300).optional(),
  broughtByPersonId: uuidSchema.nullish()
});
var vintageField = z5.number().int().min(1800).max(2100);
var yearField = z5.number().int().min(1800).max(2200);
var wineCatalogInputSchema = z5.object({
  wineName: z5.string().trim().min(1).max(300),
  producer: z5.string().trim().max(200).optional(),
  varietals: z5.array(z5.string().trim().min(1).max(200)).max(12).default([]),
  regionAva: z5.string().trim().max(200).optional(),
  country: z5.string().trim().max(120).optional(),
  vineyard: z5.string().trim().max(200).optional(),
  vintage: vintageField.nullish(),
  color: z5.enum(WINE_COLORS).nullish(),
  abv: z5.number().min(0).max(100).nullish(),
  typicalPrice: z5.number().nonnegative().max(1e6).nullish(),
  drinkFrom: yearField.nullish(),
  drinkTo: yearField.nullish(),
  labelImageUrl: optionalSafeHttpUrlSchema,
  tastingNotes: z5.string().max(5e3).optional(),
  notes: z5.string().max(5e3).optional()
});
var updateWineCatalogSchema = z5.object({
  wineName: z5.string().trim().min(1).max(300).optional(),
  producer: z5.string().trim().max(200).nullish(),
  varietals: z5.array(z5.string().trim().min(1).max(200)).max(12).optional(),
  regionAva: z5.string().trim().max(200).nullish(),
  country: z5.string().trim().max(120).nullish(),
  vineyard: z5.string().trim().max(200).nullish(),
  vintage: vintageField.nullish(),
  color: z5.enum(WINE_COLORS).nullish(),
  abv: z5.number().min(0).max(100).nullish(),
  typicalPrice: z5.number().nonnegative().max(1e6).nullish(),
  drinkFrom: yearField.nullish(),
  drinkTo: yearField.nullish(),
  labelImageUrl: nullableSafeHttpUrlSchema,
  tastingNotes: z5.string().max(5e3).nullish(),
  notes: z5.string().max(5e3).nullish()
});
var wineCatalogSearchSchema = z5.object({
  q: z5.string().trim().max(200).optional(),
  producer: z5.string().trim().max(200).optional(),
  varietal: z5.string().trim().max(200).optional(),
  regionAva: z5.string().trim().max(200).optional(),
  country: z5.string().trim().max(120).optional(),
  color: z5.enum(WINE_COLORS).optional(),
  vintage: z5.coerce.number().int().min(1800).max(2100).optional()
});
var archiveSearchSchema = z5.object({
  sort: z5.enum(["newest", "oldest", "title"]).default("newest"),
  q: z5.string().trim().max(200).optional(),
  year: z5.coerce.number().int().min(1900).max(2100).optional(),
  month: z5.coerce.number().int().min(1).max(12).optional(),
  regionLocation: z5.string().trim().max(200).optional(),
  venue: z5.string().trim().max(200).optional(),
  varietal: z5.string().trim().max(200).optional(),
  regionAva: z5.string().trim().max(200).optional(),
  producer: z5.string().trim().max(200).optional(),
  personId: uuidSchema.optional()
});
var paginationSchema = z5.object({
  page: z5.coerce.number().int().min(1).default(1),
  pageSize: z5.coerce.number().int().min(1).max(100).default(25)
});
var pushTokenInputSchema = z5.object({
  token: z5.string().trim().min(10).max(200),
  platform: z5.enum(["ios", "android"]),
  deviceId: z5.string().trim().max(120).optional()
});
var adminAnnouncementSchema = z5.object({
  title: z5.string().trim().min(1).max(120),
  body: z5.string().trim().min(1).max(500)
});

// src/types.ts
function isOtpRequired(res) {
  return "otpRequired" in res && res.otpRequired === true;
}

// src/maskEmail.ts
function maskEmail(email) {
  const at = email.indexOf("@");
  if (at <= 0) return "***";
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const visible = local.length <= 1 ? "*" : `${local[0]}${"*".repeat(Math.min(local.length - 1, 3))}`;
  return `${visible}@${domain}`;
}

// src/importSample.ts
var SAMPLE_IMPORT_FILENAME = "gwc-sample-import.csv";
var SAMPLE_IMPORT_CSV = `Event Title,Event Year,Event Month,Venue,Region / Commune,Wine Letter,Producer,Wine Name,Vintage,Varietal,Color,Country,Brought By,Member First Name,Member Last Name,Sight/Nose,Taste,Overall,Event Date,Vineyard,Wine Notes,Personal Notes
Spring Reds,2024,3,Monterey Country Club,Monterey Peninsula,A,Ridge Estate,Cabernet Sauvignon,2019,Cabernet Sauvignon,red,USA,James Whitfield,Robert,Anderson,2.25,2.5,3.75,2024-03-15,Estate vineyard,From the club archive,Good balance
Spring Reds,2024,3,Monterey Country Club,Monterey Peninsula,A,Ridge Estate,Cabernet Sauvignon,2019,Cabernet Sauvignon,red,USA,James Whitfield,Michael,Chen,2,3,3,2024-03-15,Estate vineyard,From the club archive,Good balance
Spring Reds,2024,3,Monterey Country Club,Monterey Peninsula,B,Ch\xE2teau Margaux,Ch\xE2teau Margaux,2015,Bordeaux Blend,red,France,David Harrington,Robert,Anderson,3,3,4,2024-03-15,Estate vineyard,From the club archive,Good balance
Spring Reds,2024,3,Monterey Country Club,Monterey Peninsula,B,Ch\xE2teau Margaux,Ch\xE2teau Margaux,2015,Bordeaux Blend,red,France,David Harrington,Michael,Chen,3,2,3,2024-03-15,Estate vineyard,From the club archive,Good balance
Spring Reds,2024,3,Monterey Country Club,Monterey Peninsula,C,Flowers,Pinot Noir,2020,Pinot Noir,red,USA,Thomas Reed,Robert,Anderson,2,2,3,2024-03-15,Estate vineyard,From the club archive,Good balance
Spring Reds,2024,3,Monterey Country Club,Monterey Peninsula,C,Flowers,Pinot Noir,2020,Pinot Noir,red,USA,Thomas Reed,Michael,Chen,3,3,4,2024-03-15,Estate vineyard,From the club archive,Good balance
Coastal Whites,2023,9,Pebble Beach Lodge,Monterey Peninsula,A,Domaine Leflaive,Puligny-Montrachet,2018,Chardonnay,white,France,William Foster,Robert,Anderson,3,3,4,2023-09-15,Estate vineyard,From the club archive,Good balance
Coastal Whites,2023,9,Pebble Beach Lodge,Monterey Peninsula,A,Domaine Leflaive,Puligny-Montrachet,2018,Chardonnay,white,France,William Foster,Michael,Chen,2,3,3,2023-09-15,Estate vineyard,From the club archive,Good balance
Coastal Whites,2023,9,Pebble Beach Lodge,Monterey Peninsula,B,Kistler,Sonoma Coast Chardonnay,2021,Chardonnay,white,USA,James Whitfield,Robert,Anderson,3,2,3,2023-09-15,Estate vineyard,From the club archive,Good balance
Coastal Whites,2023,9,Pebble Beach Lodge,Monterey Peninsula,B,Kistler,Sonoma Coast Chardonnay,2021,Chardonnay,white,USA,James Whitfield,Michael,Chen,3,3,4,2023-09-15,Estate vineyard,From the club archive,Good balance
`;
var SAMPLE_IMPORT_COLUMNS = [
  "Event Title",
  "Event Year",
  "Event Month",
  "Venue",
  "Region / Commune",
  "Wine Letter",
  "Producer",
  "Wine Name",
  "Vintage",
  "Varietal",
  "Color",
  "Country",
  "Brought By",
  "Member First Name",
  "Member Last Name",
  "Sight/Nose",
  "Taste",
  "Overall",
  "Event Date",
  "Vineyard",
  "Wine Notes",
  "Personal Notes"
];

// src/phaseMachine.ts
var PHASE_TRANSITIONS = {
  setup: ["scoring_open"],
  scoring_open: ["scoring_closed"],
  scoring_closed: ["revealing", "scoring_open"],
  revealing: ["final", "scoring_closed"],
  final: ["revealing"]
};
function canTransitionPhase(from, to) {
  if (from === to) return true;
  return PHASE_TRANSITIONS[from].includes(to);
}
function assertPhaseTransition(from, to) {
  if (!canTransitionPhase(from, to)) {
    throw new Error(`Invalid phase transition: ${from} \u2192 ${to}`);
  }
}

// src/billing.ts
import { z as z6 } from "zod";
var CHARGE_TYPES = ["annual_dues", "event", "custom"];
var INVOICE_STATUSES = ["draft", "open", "paid", "void"];
var PAYMENT_STATUSES = [
  "pending",
  "processing",
  "succeeded",
  "failed",
  "cancelled",
  "refunded"
];
var PAYMENT_PROVIDERS = ["manual", "stripe", "paypal"];
var createChargeSchema = z6.object({
  personId: z6.string().uuid(),
  chargeType: z6.enum(CHARGE_TYPES).default("custom"),
  label: z6.string().trim().min(1).max(160),
  amount: z6.number().positive().max(1e6),
  periodYear: z6.number().int().min(1980).max(2100).nullish(),
  dueDate: z6.string().date().nullish(),
  eventId: z6.string().uuid().nullish(),
  notes: z6.string().max(2e3).nullish()
});
var updateChargeSchema = z6.object({
  label: z6.string().trim().min(1).max(160).optional(),
  amount: z6.number().positive().max(1e6).optional(),
  status: z6.enum(DUES_STATUSES).optional(),
  dueDate: z6.string().date().nullish(),
  notes: z6.string().max(2e3).nullish(),
  paymentMethod: z6.enum(PAYMENT_METHODS).nullish()
});
var createInvoiceSchema = z6.object({
  personId: z6.string().uuid(),
  chargeIds: z6.array(z6.string().uuid()).min(1).max(50),
  dueDate: z6.string().date().nullish(),
  notes: z6.string().max(2e3).nullish(),
  issueNow: z6.boolean().default(true)
});
var checkoutSchema = z6.object({
  invoiceId: z6.string().uuid().optional(),
  chargeId: z6.string().uuid().optional(),
  provider: z6.enum(PAYMENT_PROVIDERS).default("stripe")
}).refine((v) => Boolean(v.invoiceId || v.chargeId), {
  message: "invoiceId or chargeId is required"
});
var recordManualPaymentSchema = z6.object({
  invoiceId: z6.string().uuid().optional(),
  chargeId: z6.string().uuid().optional(),
  amount: z6.number().positive().max(1e6),
  paymentMethod: z6.enum(PAYMENT_METHODS),
  notes: z6.string().max(2e3).nullish(),
  paidAt: z6.string().date().optional()
}).refine((v) => Boolean(v.invoiceId || v.chargeId), {
  message: "invoiceId or chargeId is required"
});

// src/apiEnvelope.ts
var API_V2_PREFIX = "/api/v2";

// src/importSchemas.ts
import { z as z7 } from "zod";
var CANONICAL_IMPORT_FIELDS = [
  "eventTitle",
  "eventYear",
  "eventMonth",
  "eventDate",
  "venue",
  "region",
  "wineLetter",
  "producer",
  "wineName",
  "vineyard",
  "wineNotes",
  "notes",
  "vintage",
  "varietal",
  "color",
  "country",
  "broughtBy",
  "memberFirstName",
  "memberLastName",
  "sightNose",
  "taste",
  "overall",
  // Group results — read from wine-level rows only (see isWineLevelImportRow).
  "groupScore",
  "groupRank",
  "groupAverage",
  "groupVotes"
];
var IMPORT_HEADER_ALIASES = {
  eventTitle: ["Event Title", "Event", "Tasting", "Tasting Name", "Title"],
  eventYear: ["Event Year", "Year", "Tasting Year"],
  eventMonth: ["Event Month", "Month"],
  eventDate: ["Event Date", "Tasting Date", "Date"],
  vineyard: ["Vineyard", "Cru"],
  wineNotes: ["Wine Notes", "General Wine Notes"],
  notes: ["Personal Notes", "Tasting Notes", "Notes"],
  venue: ["Venue", "Location", "Club"],
  region: ["Region / Commune", "Region/Commune", "Region", "Appellation", "AVA", "Area"],
  wineLetter: ["Wine Letter", "Letter", "Slot", "Wine #", "Wine No"],
  producer: ["Producer", "Winery", "House"],
  wineName: ["Wine Name", "Wine", "Label", "Cuv\xE9e", "Cuvee"],
  vintage: ["Vintage", "Year (Wine)", "Wine Year"],
  varietal: ["Varietal", "Grape", "Variety"],
  color: ["Color", "Colour", "Type"],
  country: ["Country", "Nation"],
  broughtBy: ["Brought By", "Presenter", "Host Wine"],
  memberFirstName: ["Member First Name", "First Name", "Scorer First", "Member First"],
  memberLastName: ["Member Last Name", "Last Name", "Scorer Last", "Member Last"],
  sightNose: ["Sight/Nose", "Sight Nose", "Sight", "Nose", "Appearance"],
  taste: ["Taste", "Palate", "Flavor"],
  overall: ["Overall", "Overall Quality", "Total Quality", "Quality"],
  groupScore: [
    "Group Score",
    "Group Total",
    "Group Points",
    "Total Points",
    "Total Score",
    "Points",
    "Total"
  ],
  groupRank: ["Group Rank", "Rank", "Place", "Placing", "Finish", "Finished", "Overall Rank", "Standing"],
  groupAverage: [
    "Group Average",
    "Group Avg",
    "Average Score",
    "Avg Score",
    "Average",
    "Avg",
    "Avg.",
    "Mean"
  ],
  groupVotes: ["Votes", "Scorers", "Tasters", "Score Count", "No. of Scores", "Number of Scores", "# Scores"]
};
var optionalInt = z7.preprocess(
  (v) => v === "" || v == null ? null : Number(v),
  z7.number().int().nullable()
);
var optionalScore = z7.preprocess(
  (v) => v === "" || v == null ? null : Number(v),
  z7.number().multipleOf(0.25).nullable()
);
var optionalName = z7.preprocess(
  (v) => typeof v === "string" && v.trim() === "" ? null : v,
  z7.string().trim().min(1).nullish()
);
var groupNumber = (schema) => z7.preprocess((v) => {
  if (v === "" || v == null) return null;
  if (typeof v !== "string") return v;
  const m = v.trim().match(/^(?:t|=|tied\s*)?(\d+(?:\.\d+)?)\s*(?:st|nd|rd|th|pts?\.?|points?)?$/i);
  return m ? Number(m[1]) : Number(v);
}, schema.nullable());
var importRowSchema = z7.object({
  eventTitle: z7.string().trim().min(1),
  eventYear: z7.preprocess((v) => Number(v), z7.number().int().min(1900).max(2100)),
  eventMonth: optionalInt,
  eventDate: z7.preprocess(
    (v) => v === "" || v == null ? null : v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === "number" ? new Date(Date.UTC(1899, 11, 30) + v * 864e5).toISOString().slice(0, 10) : v,
    z7.string().date().nullish()
  ),
  vineyard: z7.string().trim().max(200).nullish(),
  wineNotes: z7.string().max(5e3).nullish(),
  notes: z7.string().max(2e3).nullish(),
  venue: z7.string().trim().nullish(),
  region: z7.string().trim().nullish(),
  wineLetter: z7.preprocess((v) => {
    const n = typeof v === "number" ? v : typeof v === "string" && /^\s*\d+\s*$/.test(v) ? Number(v) : NaN;
    if (Number.isInteger(n) && n >= 1 && n <= WINE_LETTERS.length) return WINE_LETTERS[n - 1];
    return typeof v === "string" ? v.trim().toUpperCase() : v;
  }, z7.enum(WINE_LETTERS)),
  producer: z7.string().trim().nullish(),
  wineName: z7.string().trim().nullish(),
  vintage: optionalInt,
  varietal: z7.string().trim().nullish(),
  color: z7.preprocess(
    (v) => typeof v === "string" ? v.trim().toLowerCase() || null : v,
    z7.enum(WINE_COLORS).nullish()
  ),
  country: z7.string().trim().nullish(),
  broughtBy: z7.string().trim().nullish(),
  memberFirstName: optionalName,
  memberLastName: optionalName,
  sightNose: optionalScore,
  taste: optionalScore,
  overall: optionalScore,
  groupScore: groupNumber(z7.number().min(0).max(1e4)).optional(),
  groupRank: groupNumber(z7.number().int().min(1).max(WINE_LETTERS.length)).optional(),
  groupAverage: groupNumber(z7.number().min(0).max(1e3)).optional(),
  groupVotes: groupNumber(z7.number().int().min(0).max(1e3)).optional()
}).superRefine((row, ctx) => {
  const hasFirst = !!row.memberFirstName;
  const hasLast = !!row.memberLastName;
  if (hasFirst !== hasLast)
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [hasFirst ? "memberLastName" : "memberFirstName"],
      message: "Give the member's first and last name, or leave both blank for a wine-level row"
    });
  if (!hasFirst && !hasLast && (row.sightNose != null || row.taste != null || row.overall != null))
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: ["memberFirstName"],
      message: "Scores need the member who gave them"
    });
  if (row.eventDate && (Number(row.eventDate.slice(0, 4)) !== row.eventYear || row.eventMonth != null && Number(row.eventDate.slice(5, 7)) !== row.eventMonth))
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: ["eventDate"],
      message: "Date must agree with the tasting year and month"
    });
});
function isWineLevelImportRow(row) {
  return !row.memberFirstName && !row.memberLastName;
}
var columnMappingSchema = z7.record(z7.enum(CANONICAL_IMPORT_FIELDS), z7.string().min(1));
function validateImportScores(row) {
  const issues = validateComponents(
    { sightNose: row.sightNose, taste: row.taste, overall: row.overall },
    DEFAULT_SCORING_SCHEME
  );
  return issues.map((i) => i.message);
}
function normalizeHeader(h) {
  return h.trim().toLowerCase().replace(/\s+/g, " ");
}
function autoDetectMapping(headers) {
  const mapping = {};
  const normalizedHeaders = headers.map((h) => ({ raw: h, norm: normalizeHeader(h) }));
  for (const field of CANONICAL_IMPORT_FIELDS) {
    const aliases = IMPORT_HEADER_ALIASES[field].map(normalizeHeader);
    const hit = normalizedHeaders.find((h) => aliases.includes(h.norm));
    if (hit) mapping[field] = hit.raw;
  }
  const required = ["eventTitle", "eventYear", "wineLetter"];
  const missing = required.filter((f) => !mapping[f]);
  if (missing.length > 0) {
    throw new Error(`Could not auto-detect columns: ${missing.join(", ")}`);
  }
  return mapping;
}
var SAMPLE_GROUP_IMPORT_FILENAME = "gwc-sample-group-results.csv";
var SAMPLE_GROUP_IMPORT_CSV = `Tasting,Year,Month,Date,Venue,Wine #,Producer,Wine,Vintage,Region,Varietal,Color,Brought By,Total Points,Avg,Votes,Place
Burgundy Night,2019,11,2019-11-14,Monterey Country Club,A,Domaine Leflaive,Puligny-Montrachet,2014,Puligny-Montrachet,Chardonnay,white,William Foster,92.5,7.71,12,2nd
Burgundy Night,2019,11,2019-11-14,Monterey Country Club,B,Joseph Drouhin,Clos des Mouches,2015,Beaune,Pinot Noir,red,James Whitfield,78.5,6.54,12,4th
Burgundy Night,2019,11,2019-11-14,Monterey Country Club,C,Domaine Faiveley,Gevrey-Chambertin,2016,Gevrey-Chambertin,Pinot Noir,red,Thomas Reed,96,8.00,12,1st
Burgundy Night,2019,11,2019-11-14,Monterey Country Club,D,Louis Jadot,Chassagne-Montrachet,2017,Chassagne-Montrachet,Chardonnay,white,David Harrington,84.25,7.02,12,3rd
`;

// src/stagingHash.ts
function stagingContentHash(normalized) {
  const basis = JSON.stringify(normalized);
  let h = 2166136261;
  for (let i = 0; i < basis.length; i++) {
    h ^= basis.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

// src/archiveEntry.ts
import { z as z8 } from "zod";
var archiveEntrySchema = z8.object({
  eventId: z8.string().uuid().optional(),
  title: z8.string().trim().min(1).max(200),
  eventDate: z8.string().date(),
  personId: z8.string().uuid(),
  wine: wineInputSchema,
  score: makeScoreInputSchema().pick({ sightNose: true, taste: true, overall: true, notes: true }),
  labelImageUrl: optionalSafeHttpUrlSchema,
  attachments: z8.array(
    z8.object({
      mediaId: z8.string().uuid(),
      type: z8.enum(["label", "menu", "scorecard", "photo"])
    })
  ).max(20).default([])
});

// src/reporting.ts
import { z as z9 } from "zod";
var REPORT_FIELDS = {
  tasting: "Tasting",
  date: "Date",
  year: "Year",
  theme: "Theme",
  letter: "Wine letter",
  wine: "Wine",
  producer: "Producer",
  region: "Region / Commune",
  vineyard: "Vineyard",
  varietal: "Varietal / Type",
  vintage: "Vintage",
  broughtBy: "Brought by",
  score: "Group score",
  average: "Group average",
  rank: "Rank",
  votes: "Votes"
};
var REPORT_OPERATORS = [
  "includes",
  "excludes",
  "equals",
  "not_equals",
  "greater_than",
  "less_than"
];
var fieldSchema = z9.enum(Object.keys(REPORT_FIELDS));
var reportInputSchema = z9.object({
  fields: z9.array(fieldSchema).min(1).max(16).refine((v) => new Set(v).size === v.length, "Choose each field once"),
  filters: z9.array(
    z9.object({
      field: fieldSchema,
      operator: z9.enum(REPORT_OPERATORS),
      value: z9.string().max(200)
    })
  ).max(20).default([]),
  match: z9.enum(["all", "any"]).default("all"),
  sort: fieldSchema.default("date"),
  direction: z9.enum(["asc", "desc"]).default("desc"),
  format: z9.enum(["json", "csv", "pdf"]).default("json")
});
function matchesReport(value, operator, query) {
  const actual = String(value ?? "").toLocaleLowerCase();
  const wanted = query.toLocaleLowerCase();
  switch (operator) {
    case "includes":
      return actual.includes(wanted);
    case "excludes":
      return !actual.includes(wanted);
    case "equals":
      return actual === wanted;
    case "not_equals":
      return actual !== wanted;
    case "greater_than":
      return value != null && query.trim() !== "" && Number.isFinite(Number(value)) && Number(value) > Number(query);
    case "less_than":
      return value != null && query.trim() !== "" && Number.isFinite(Number(value)) && Number(value) < Number(query);
  }
}
function reportCsv(fields, rows) {
  const cell = (v) => {
    let s = String(v ?? "");
    if (typeof v === "string" && /^[\s]*[=+\-@\t\r]/.test(s)) s = `'${s}`;
    return `"${s.replace(/"/g, '""')}"`;
  };
  return "\uFEFF" + [fields, ...rows].map((row) => row.map(cell).join(",")).join("\r\n") + "\r\n";
}

// src/historyContribution.ts
import { z as z10 } from "zod";
var minTotal = (s) => s.sightNose.min + s.taste.min + s.overall.min;
var str = (max) => z10.string().trim().max(max).nullish();
var quarter = (range) => z10.number().multipleOf(0.25, "Use quarter-point increments").min(range.min).max(range.max).nullish();
var contributionPersonRefSchema = z10.object({
  personId: uuidSchema.nullish(),
  name: str(120)
});
var contributionAttendeeSchema = contributionPersonRefSchema.extend({
  isGuest: z10.boolean().nullish(),
  notes: str(300)
});
var contributionWineSchema = z10.object({
  /** Stable row id generated by the form so scores can point at an unlettered wine. */
  clientKey: z10.string().trim().max(40).nullish(),
  letter: z10.enum(WINE_LETTERS).nullish(),
  name: str(300),
  producer: str(200),
  vintage: z10.number().int().min(1800).max(2100).nullish(),
  region: str(200),
  varietal: str(200),
  vineyard: str(200),
  color: z10.enum(WINE_COLORS).nullish(),
  broughtBy: contributionPersonRefSchema.nullish(),
  groupScore: z10.number().min(0).max(1e3).nullish(),
  groupRank: z10.number().int().min(1).max(20).nullish(),
  price: z10.number().nonnegative().max(1e5).nullish(),
  notes: str(2e3)
});
var contributionScoreSchema = z10.object({
  person: contributionPersonRefSchema.nullish(),
  /** Either the letter or the wine row's clientKey identifies the wine. */
  letter: z10.enum(WINE_LETTERS).nullish(),
  wineKey: z10.string().trim().max(40).nullish(),
  sightNose: quarter(DEFAULT_SCORING_SCHEME.sightNose),
  taste: quarter(DEFAULT_SCORING_SCHEME.taste),
  overall: quarter(DEFAULT_SCORING_SCHEME.overall),
  /** Used only when the components are unknown (e.g. a card with just a total). */
  total: z10.number().min(minTotal(DEFAULT_SCORING_SCHEME)).max(maxTotal(DEFAULT_SCORING_SCHEME)).nullish(),
  notes: str(2e3)
});
var contributionEventSchema = z10.object({
  title: str(200),
  year: z10.number().int().min(1900).max(2100).nullish(),
  month: z10.number().int().min(1).max(12).nullish(),
  day: z10.number().int().min(1).max(31).nullish(),
  venue: str(200),
  regionLocation: str(200),
  host: str(120),
  theme: str(200),
  dressCode: str(120),
  price: z10.number().nonnegative().max(1e5).nullish(),
  menu: str(5e3),
  notes: str(5e3)
});
var CONTRIBUTION_CONFIDENCES = ["certain", "fairly_sure", "vague"];
var historyContributionSchema = z10.object({
  event: contributionEventSchema.nullish(),
  attendees: z10.array(contributionAttendeeSchema).max(60).nullish(),
  wines: z10.array(contributionWineSchema).max(WINE_LETTERS.length).nullish(),
  scores: z10.array(contributionScoreSchema).max(400).nullish(),
  /** Memories, anecdotes, anything narrative. */
  narrative: str(1e4),
  /** "From my 1994 notebook", "Bob's photo album", … */
  sourceDescription: str(1e3),
  confidence: z10.enum(CONTRIBUTION_CONFIDENCES).nullish()
}).strict();
var EMPTY_CONTRIBUTION = {};
var createContributionSchema = z10.object({
  linkedEventId: uuidSchema.nullish(),
  payload: historyContributionSchema.optional()
});
var updateContributionSchema = z10.object({
  payload: historyContributionSchema
});
var reviewContributionSchema = z10.object({
  status: z10.enum(["needs_info", "declined", "submitted"]),
  /** Shown to the contributor. */
  reviewNotes: z10.string().trim().max(2e3).nullish(),
  /** Reviewer-only. */
  internalNotes: z10.string().trim().max(2e3).nullish()
});
var mergeContributionSchema = z10.object({
  title: z10.string().trim().min(1).max(200),
  eventYear: z10.number().int().min(1900).max(2100),
  eventMonth: z10.number().int().min(1).max(12).nullish(),
  eventDay: z10.number().int().min(1).max(31).nullish(),
  /** When the contribution was started from an archived tasting, merge into it. */
  useLinkedEvent: z10.boolean().default(true),
  /** Reviewer's final letter for each wine row, keyed by clientKey or row index. */
  letters: z10.record(z10.string(), z10.enum(WINE_LETTERS)).default({}),
  /** Overrides for free-text names → a person id (key = normalized name). */
  personMap: z10.record(z10.string(), uuidSchema).default({}),
  /** Create non-member people rows for names that match nobody. */
  createUnknownPeople: z10.boolean().default(true),
  /** Replace an existing score for the same (wine, person) instead of skipping it. */
  overwriteScores: z10.boolean().default(false),
  /** Replace event/wine fields that are already filled instead of only filling blanks. */
  overwriteEventFields: z10.boolean().default(false)
}).refine((v) => v.eventDay == null || v.eventMonth != null, {
  message: "A day needs a month",
  path: ["eventDay"]
});
function normalizePersonName(name) {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}
function contributionWineKey(wine, index) {
  return wine.clientKey?.trim() || String(index);
}

// src/lookups.ts
import { z as z11 } from "zod";
var WINE_LOOKUP_KINDS = ["producers", "regions", "vineyards", "varietals"];
var WINE_LOOKUP_LABELS = {
  producers: { one: "Producer", many: "Producers" },
  regions: { one: "Region / Commune", many: "Regions & communes" },
  vineyards: { one: "Vineyard", many: "Vineyards" },
  varietals: { one: "Varietal", many: "Varietals" }
};
var REGION_KINDS = ["country", "region", "ava", "appellation", "commune"];
var queryBoolean = z11.union([z11.boolean(), z11.enum(["true", "false"])]).transform((v) => v === true || v === "true");
var lookupSearchSchema = z11.object({
  q: z11.string().trim().max(200).optional(),
  /**
   * Regions: only this side's (plus unclassified ones). Ignored for other kinds,
   * except varietals, which return nothing for `old` (the grape is implied).
   */
  world: z11.enum(WINE_WORLDS).optional(),
  /** Vineyards: prefer those in this region (others still match, ranked after). */
  regionId: z11.string().uuid().optional(),
  limit: z11.coerce.number().int().min(1).max(50).default(20)
});
var lookupCreateSchema = z11.object({
  name: z11.string().trim().min(1).max(200),
  /** Regions only. */
  world: z11.enum(WINE_WORLDS).nullish(),
  /** Regions only: the containing region ("Côte de Beaune" for "Meursault"). */
  parentId: z11.string().uuid().nullish(),
  /** Vineyards (where it is) and producers (home region). */
  regionId: z11.string().uuid().nullish(),
  /** Varietals only. */
  color: z11.enum(WINE_COLORS).nullish()
});
var lookupManageQuerySchema = z11.object({
  q: z11.string().trim().max(200).optional(),
  needsReview: queryBoolean.optional(),
  page: z11.coerce.number().int().min(1).default(1),
  pageSize: z11.coerce.number().int().min(1).max(200).default(50)
});
var lookupUpdateSchema = z11.object({
  name: z11.string().trim().min(1).max(200).optional(),
  world: z11.enum(WINE_WORLDS).nullish(),
  parentId: z11.string().uuid().nullish(),
  regionKind: z11.enum(REGION_KINDS).nullish(),
  regionId: z11.string().uuid().nullish(),
  color: z11.enum(WINE_COLORS).nullish(),
  /** true stamps reviewed_at/by and clears needs_review; false flags it again. */
  reviewed: z11.boolean().optional()
});
var lookupMergeSchema = z11.object({ intoId: z11.string().uuid() });
var lookupReviewSchema = z11.object({
  ids: z11.array(z11.string().uuid()).min(1).max(200)
});

// src/lookupSeeds.ts
var communes = (...names) => names.map((name) => ({ name, kind: "commune" }));
var avas = (...names) => names.map((name) => ({ name, kind: "ava" }));
var appellations = (...names) => names.map((name) => ({ name, kind: "appellation" }));
var DEFAULT_REGION_SEEDS = [
  {
    name: "United States",
    kind: "country",
    world: "new",
    children: [
      {
        name: "California",
        kind: "region",
        children: [
          {
            name: "Napa Valley",
            kind: "ava",
            children: avas(
              "Oakville",
              "Rutherford",
              "Stags Leap District",
              "St. Helena",
              "Calistoga",
              "Howell Mountain",
              "Mount Veeder",
              "Spring Mountain District",
              "Coombsville"
            )
          },
          { name: "Los Carneros", kind: "ava" },
          {
            name: "Sonoma County",
            kind: "region",
            children: avas(
              "Russian River Valley",
              "Sonoma Coast",
              "Alexander Valley",
              "Dry Creek Valley",
              "Sonoma Valley"
            )
          },
          {
            name: "Monterey",
            kind: "ava",
            children: [
              { name: "Salinas Valley", kind: "region" },
              ...avas("Santa Lucia Highlands", "Carmel Valley", "Arroyo Seco", "Chalone")
            ]
          },
          { name: "Santa Cruz Mountains", kind: "ava" },
          { name: "Paso Robles", kind: "ava" },
          { name: "Edna Valley", kind: "ava" },
          {
            name: "Santa Barbara County",
            kind: "region",
            children: avas("Sta. Rita Hills", "Santa Maria Valley", "Ballard Canyon")
          },
          { name: "Anderson Valley", kind: "ava" },
          { name: "Lodi", kind: "ava" }
        ]
      },
      { name: "Oregon", kind: "region", children: avas("Willamette Valley") },
      {
        name: "Washington",
        kind: "region",
        children: avas("Columbia Valley", "Walla Walla Valley", "Red Mountain")
      }
    ]
  },
  {
    name: "France",
    kind: "country",
    world: "old",
    children: [
      {
        name: "Burgundy",
        kind: "region",
        children: [
          { name: "Chablis", kind: "appellation" },
          {
            name: "C\xF4te de Nuits",
            kind: "region",
            children: communes(
              "Gevrey-Chambertin",
              "Morey-Saint-Denis",
              "Chambolle-Musigny",
              "Vougeot",
              "Vosne-Roman\xE9e",
              "Nuits-Saint-Georges",
              "Marsannay"
            )
          },
          {
            name: "C\xF4te de Beaune",
            kind: "region",
            children: communes(
              "Meursault",
              "Puligny-Montrachet",
              "Chassagne-Montrachet",
              "Saint-Aubin",
              "Beaune",
              "Pommard",
              "Volnay",
              "Aloxe-Corton",
              "Savigny-l\xE8s-Beaune",
              "Santenay",
              "Auxey-Duresses"
            )
          },
          { name: "C\xF4te Chalonnaise", kind: "region", children: communes("Mercurey", "Givry", "Rully") },
          { name: "M\xE2connais", kind: "region", children: communes("Pouilly-Fuiss\xE9", "Saint-V\xE9ran") },
          { name: "Bourgogne", kind: "appellation" }
        ]
      },
      {
        name: "Bordeaux",
        kind: "region",
        children: [
          {
            name: "M\xE9doc",
            kind: "appellation",
            children: communes("Pauillac", "Margaux", "Saint-Julien", "Saint-Est\xE8phe", "Listrac", "Moulis")
          },
          { name: "Haut-M\xE9doc", kind: "appellation" },
          { name: "Pessac-L\xE9ognan", kind: "appellation" },
          { name: "Graves", kind: "appellation" },
          { name: "Sauternes", kind: "appellation" },
          { name: "Saint-\xC9milion", kind: "appellation" },
          { name: "Pomerol", kind: "appellation" },
          { name: "Bordeaux Sup\xE9rieur", kind: "appellation" }
        ]
      },
      {
        name: "Rh\xF4ne",
        kind: "region",
        children: appellations(
          "C\xF4te-R\xF4tie",
          "Condrieu",
          "Hermitage",
          "Crozes-Hermitage",
          "Saint-Joseph",
          "Cornas",
          "Ch\xE2teauneuf-du-Pape",
          "Gigondas",
          "Vacqueyras",
          "C\xF4tes du Rh\xF4ne"
        )
      },
      {
        name: "Loire",
        kind: "region",
        children: appellations("Sancerre", "Pouilly-Fum\xE9", "Vouvray", "Chinon", "Savenni\xE8res", "Muscadet")
      },
      { name: "Champagne", kind: "region" },
      { name: "Alsace", kind: "region" },
      {
        name: "Beaujolais",
        kind: "region",
        children: communes("Morgon", "Fleurie", "Moulin-\xE0-Vent", "Brouilly")
      },
      { name: "Languedoc-Roussillon", kind: "region" },
      { name: "Provence", kind: "region" }
    ]
  },
  {
    name: "Italy",
    kind: "country",
    world: "old",
    children: [
      { name: "Piedmont", kind: "region", children: appellations("Barolo", "Barbaresco", "Barbera d\u2019Asti", "Gavi") },
      {
        name: "Tuscany",
        kind: "region",
        children: appellations(
          "Chianti Classico",
          "Brunello di Montalcino",
          "Vino Nobile di Montepulciano",
          "Bolgheri"
        )
      },
      { name: "Veneto", kind: "region", children: appellations("Valpolicella", "Amarone della Valpolicella", "Soave", "Prosecco") },
      { name: "Friuli", kind: "region" },
      { name: "Sicily", kind: "region", children: appellations("Etna") },
      { name: "Campania", kind: "region", children: appellations("Taurasi") }
    ]
  },
  {
    name: "Spain",
    kind: "country",
    world: "old",
    children: appellations("Rioja", "Ribera del Duero", "Priorat", "R\xEDas Baixas", "Toro", "Jerez", "Cava")
  },
  {
    name: "Germany",
    kind: "country",
    world: "old",
    children: [
      { name: "Mosel", kind: "region" },
      { name: "Rheingau", kind: "region" },
      { name: "Pfalz", kind: "region" },
      { name: "Nahe", kind: "region" },
      { name: "Rheinhessen", kind: "region" }
    ]
  },
  { name: "Portugal", kind: "country", world: "old", children: appellations("Douro", "Porto", "Madeira") },
  { name: "Austria", kind: "country", world: "old", children: [{ name: "Wachau", kind: "region" }] },
  {
    name: "Australia",
    kind: "country",
    world: "new",
    children: [
      { name: "Barossa Valley", kind: "region" },
      { name: "McLaren Vale", kind: "region" },
      { name: "Margaret River", kind: "region" },
      { name: "Coonawarra", kind: "region" }
    ]
  },
  {
    name: "New Zealand",
    kind: "country",
    world: "new",
    children: [
      { name: "Marlborough", kind: "region" },
      { name: "Central Otago", kind: "region" }
    ]
  },
  { name: "Argentina", kind: "country", world: "new", children: [{ name: "Mendoza", kind: "region" }] },
  { name: "Chile", kind: "country", world: "new", children: [{ name: "Maipo Valley", kind: "region" }] },
  { name: "South Africa", kind: "country", world: "new", children: [{ name: "Stellenbosch", kind: "region" }] }
];
var DEFAULT_VARIETAL_SEEDS = [
  ...[
    "Cabernet Sauvignon",
    "Merlot",
    "Cabernet Franc",
    "Petit Verdot",
    "Malbec",
    "Pinot Noir",
    "Syrah",
    "Grenache",
    "Mourv\xE8dre",
    "Zinfandel",
    "Petite Sirah",
    "Sangiovese",
    "Nebbiolo",
    "Barbera",
    "Tempranillo",
    "Gamay",
    "Carm\xE9n\xE8re",
    "Red Blend",
    "Bordeaux Blend",
    "Rh\xF4ne Blend"
  ].map((name) => ({ name, color: "red" })),
  ...[
    "Chardonnay",
    "Sauvignon Blanc",
    "Riesling",
    "Pinot Gris",
    "Chenin Blanc",
    "Viognier",
    "Gew\xFCrztraminer",
    "S\xE9millon",
    "Albari\xF1o",
    "Gr\xFCner Veltliner",
    "Marsanne",
    "Roussanne",
    "White Blend"
  ].map((name) => ({ name, color: "white" })),
  { name: "Ros\xE9 Blend", color: "rose" },
  { name: "Sparkling Blend", color: "sparkling" }
];
function flattenRegionSeeds(seeds = DEFAULT_REGION_SEEDS) {
  const out = [];
  const walk = (nodes, world, path) => {
    for (const n of nodes) {
      const w = n.world ?? world ?? "new";
      out.push({ name: n.name, kind: n.kind, world: w, parentName: path[path.length - 1] ?? null, path });
      if (n.children) walk(n.children, w, [...path, n.name]);
    }
  };
  walk(seeds, void 0, []);
  return out;
}

// src/tastingDashboard.ts
import { z as z12 } from "zod";
var NUDGE_REASONS = ["not_rsvpd", "unpaid", "no_wine", "no_label"];
var NUDGE_REASON_LABELS = {
  not_rsvpd: "Hasn't replied",
  unpaid: "Not paid",
  no_wine: "No wine entered",
  no_label: "No label photo"
};
var wineReviewSchema = z12.object({ reviewed: z12.boolean() });
var nudgeRequestSchema = z12.object({
  reason: z12.enum(NUDGE_REASONS),
  personIds: z12.array(z12.string().uuid()).min(1).max(200).optional(),
  /** A line from the host added above the standard copy. */
  note: z12.string().trim().max(1e3).optional(),
  channels: z12.array(z12.enum(SENDABLE_CHANNELS)).min(1).max(2).default(["push", "email"])
});

// src/reveal.ts
import { z as z13 } from "zod";
var assignSlotSchema = z13.object({
  registrationId: z13.string().uuid().nullable()
});
var revealWineSchema = z13.object({
  registrationId: z13.string().uuid().optional()
});
var revealBringerSchema = z13.object({
  registrationId: z13.string().uuid().optional(),
  personId: z13.string().uuid().optional()
}).refine((v) => !(v.registrationId && v.personId), {
  message: "Give a registration or a person, not both"
});

// src/wrapup.ts
import { z as z14 } from "zod";
var WRAPUP_AUDIENCES = ["attended", "all_members"];
var WRAPUP_AUDIENCE_LABELS = {
  attended: "Those who attended",
  all_members: "All members"
};
var MAX_WRAPUP_PHOTOS = 12;
var wrapupDraftSchema = z14.object({
  blurb: z14.string().trim().max(5e3).nullish(),
  /** Approved `media` rows of this event, in the order they should appear. */
  mediaIds: z14.array(z14.string().uuid()).max(MAX_WRAPUP_PHOTOS).default([]),
  audience: z14.enum(WRAPUP_AUDIENCES).default("attended")
});
var wrapupPreviewSchema = z14.object({
  personId: z14.string().uuid().optional()
});
var wrapupSendSchema = z14.object({
  test: z14.boolean().default(false)
});
export {
  ACCOUNT_STATUSES,
  ALL_MEMBERS,
  API_PREFIX,
  API_V2_PREFIX,
  AUDIENCE_RULE_LABELS,
  CANONICAL_IMPORT_FIELDS,
  CHARGE_TYPES,
  CONTRIBUTION_CONFIDENCES,
  CONTRIBUTION_EXTENSIONS,
  CONTRIBUTION_FILE_KINDS,
  COOKIE_CONSENT_CHOICES,
  DATE_PRECISIONS,
  DEFAULT_INVITED_MEMBER_TYPES,
  DEFAULT_NOTIFICATION_PREFERENCES,
  DEFAULT_REGION_SEEDS,
  DEFAULT_REMINDER_DAYS,
  DEFAULT_SCORING_SCHEME,
  DEFAULT_TEMPLATES,
  DEFAULT_VARIETAL_SEEDS,
  DELIVERY_CHANNELS,
  DELIVERY_STATUSES,
  DOCUMENT_EXTENSIONS,
  DUES_STATUSES,
  EMPTY_CONTRIBUTION,
  EVENT_AUDIENCE_LABELS,
  EVENT_AUDIENCE_PRESETS,
  EVENT_PHASES,
  EVENT_STATUSES,
  EVENT_WORLD_SCOPES,
  EVENT_WORLD_SCOPE_LABELS,
  GUEST_MEMBER_TYPES,
  HISTORY_CONTRIBUTION_STATUSES,
  IMAGE_EXTENSIONS,
  IMAGE_MIME_TYPES,
  IMPORT_BATCH_STATUSES,
  IMPORT_EXTENSIONS,
  IMPORT_HEADER_ALIASES,
  IMPORT_MATCH_THRESHOLD,
  IMPORT_MIME_TYPES,
  INACTIVE_MEMBER_TYPES,
  INVITE_LINK_TTL_HOURS,
  INVOICE_STATUSES,
  LOOKUP_FOLD_FROM,
  LOOKUP_FOLD_TO,
  MAX_CONTRIBUTION_BYTES,
  MAX_CONTRIBUTION_FILES,
  MAX_IMAGE_BYTES,
  MAX_IMPORT_BYTES,
  MAX_WRAPUP_PHOTOS,
  MEDIA_TYPES,
  MEMBER_TYPES,
  MEMBER_TYPE_LABELS,
  MERGE_FIELDS,
  MESSAGE_CATEGORIES,
  MESSAGE_CATEGORY_LABELS,
  MESSAGE_KINDS,
  MESSAGE_ORIGINS,
  MESSAGE_STATUSES,
  NUDGE_REASONS,
  NUDGE_REASON_LABELS,
  OTP_TTL_MINUTES,
  PAYMENT_METHODS,
  PAYMENT_PROVIDERS,
  PAYMENT_STATUSES,
  PERMISSIONS,
  PHASE_TRANSITIONS,
  RECORD_STATUSES,
  REGION_KINDS,
  REGISTRATION_PAYMENT_LABELS,
  REGISTRATION_PAYMENT_STATUSES,
  REGISTRATION_STATUSES,
  REPORT_FIELDS,
  REPORT_OPERATORS,
  ROLES,
  ROLE_LABELS,
  RSVP_BLOCKED_MESSAGES,
  RSVP_BLOCKED_REASONS,
  RSVP_ERROR_CODES,
  SAMPLE_GROUP_IMPORT_CSV,
  SAMPLE_GROUP_IMPORT_FILENAME,
  SAMPLE_IMPORT_COLUMNS,
  SAMPLE_IMPORT_CSV,
  SAMPLE_IMPORT_FILENAME,
  SAMPLE_MERGE_VARS,
  SENDABLE_CHANNELS,
  SETTLED_PAYMENT_STATUSES,
  STAGING_ROW_STATUSES,
  TEMPLATE_SCOPES,
  WINE_COLORS,
  WINE_FIELD_LABELS,
  WINE_LETTERS,
  WINE_LOOKUP_KINDS,
  WINE_LOOKUP_LABELS,
  WINE_WORLDS,
  WINE_WORLD_LABELS,
  WRAPUP_AUDIENCES,
  WRAPUP_AUDIENCE_LABELS,
  acceptInviteSchema,
  adminAnnouncementSchema,
  adminRegisterSchema,
  adminUpdateMemberSchema,
  adminUpdateRegistrationSchema,
  aggregateEventScores,
  applyMergeFields,
  archiveEntrySchema,
  archiveSearchSchema,
  assertPhaseTransition,
  assignRanksDescending,
  assignSlotSchema,
  audiencePreviewSchema,
  audienceRuleSchema,
  audienceSpecSchema,
  autoDetectMapping,
  can,
  canTransitionPhase,
  changePasswordSchema,
  channelPreferenceSchema,
  chargeDuesSchema,
  checkoutSchema,
  columnMappingSchema,
  composeMessageSchema,
  computeTotal,
  contributionAttendeeSchema,
  contributionEventSchema,
  contributionPersonRefSchema,
  contributionScoreSchema,
  contributionWineKey,
  contributionWineSchema,
  createChargeSchema,
  createContributionSchema,
  createInvoiceSchema,
  cuveeFromCatalogName,
  deviceConsentSchema,
  effectiveWineWorld,
  emailSchema,
  eventAudienceSpec,
  eventCommsSendSchema,
  eventInputSchema,
  eventInviteSchema,
  flattenRegionSeeds,
  foldLookupName,
  forgotPasswordSchema,
  hasRole,
  historyContributionSchema,
  importRowSchema,
  inviteExistingPersonSchema,
  inviteMemberSchema,
  invitedMemberTypesSchema,
  isGuestMemberType,
  isInactiveMemberType,
  isInvitedMemberType,
  isOtpRequired,
  isPaymentSettled,
  isWineLevelImportRow,
  justPeople,
  loginSchema,
  lookupCreateSchema,
  lookupManageQuerySchema,
  lookupMergeSchema,
  lookupReviewSchema,
  lookupSearchSchema,
  lookupUpdateSchema,
  makeScoreInputSchema,
  maskEmail,
  matchesReport,
  maxTotal,
  memberTypeLabel,
  mergeContributionSchema,
  mergeFieldsUsed,
  normalizePersonName,
  notificationPreferencesSchema,
  nudgeRequestSchema,
  nullableSafeHttpUrlSchema,
  optionalSafeHttpUrlSchema,
  paginationSchema,
  parseNotificationPreferences,
  passwordSchema,
  pushTokenInputSchema,
  putWineCategoriesSchema,
  recordManualPaymentSchema,
  reminderDaysSchema,
  reportCsv,
  reportInputSchema,
  resetPasswordOtpSchema,
  resetPasswordSchema,
  revealBringerSchema,
  revealWineSchema,
  reviewContributionSchema,
  rsvpInputSchema,
  safeHttpUrlSchema,
  scoreInputSchema,
  setTestModeSchema,
  setupSchema,
  showsVarietal,
  stagingContentHash,
  structuredWineDisplay,
  structuredWineInputSchema,
  structuredWineName,
  templateInputSchema,
  testParticipantInputSchema,
  updateChargeSchema,
  updateContributionSchema,
  updateDuesSchema,
  updateEventSchema,
  updateProfileSchema,
  updateTemplateSchema,
  updateWineCatalogSchema,
  uuidSchema,
  validateComponents,
  validateImportScores,
  verifyOtpSchema,
  wineCatalogInputSchema,
  wineCatalogSearchSchema,
  wineCategoriesSchema,
  wineCategoryInputSchema,
  wineInputSchema,
  wineReviewSchema,
  wrapupDraftSchema,
  wrapupPreviewSchema,
  wrapupSendSchema
};
//# sourceMappingURL=index.js.map